import { useEffect, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { useKeySequence } from "./useKeySequence";

// Uppercase letters: matching `event.key` requires Shift/Caps, so the sequence
// is not entered by accident during normal typing.
const SEQUENCE = ["B", "I", "R", "D"] as const;

const OVERLAY_STYLE_ID = "kbd-nav-overlay-anim";

// Overlay stacking, kept at the 32-bit-int ceiling so the overlay sits above all
// app chrome. The three layers are ordered so the dark backdrop sits *below* the
// decorative rain/confetti (otherwise the scrim dims them) while the message card
// stays on top. The decorations are aria-hidden and pointer-events:none, so every
// click falls through to the backdrop button regardless of which layer it lands on.
const Z_BACKDROP = 2147483645;
const Z_DECOR = 2147483646;
const Z_CARD = 2147483647;

const OVERLAY_ANIM_CSS = `
@keyframes kbd-nav-fall {
	0% { transform: translateY(-10vh) rotate(0deg); opacity: 0; }
	10% { opacity: 1; }
	100% { transform: translateY(110vh) rotate(360deg); opacity: 1; }
}
.kbd-nav-emoji {
	position: fixed;
	top: 0;
	font-size: 2.5rem;
	animation: kbd-nav-fall linear infinite;
	will-change: transform;
	pointer-events: none;
	z-index: ${Z_DECOR};
}
@keyframes kbd-nav-confetti {
	0% { transform: translate(0, 0) rotate(0deg); opacity: 1; }
	100% { transform: translate(var(--dx), var(--dy)) rotate(var(--spin)); opacity: 0; }
}
.kbd-nav-confetti {
	position: fixed;
	bottom: 50vh;
	line-height: 1;
	animation: kbd-nav-confetti cubic-bezier(0.15, 0.6, 0.5, 1) forwards;
	will-change: transform, opacity;
	pointer-events: none;
	z-index: ${Z_DECOR};
}
.kbd-nav-plate {
	position: relative;
	display: inline-flex;
	align-items: stretch;
	height: 4.2rem;
	background: #fff;
	border: 2px solid #111;
	border-radius: 0.5rem;
	overflow: hidden;
	box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
	font-family: "Arial Narrow", "Helvetica Neue", Arial, sans-serif;
}
.kbd-nav-plate-band {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: space-between;
	background: #003087;
	padding: 0.3rem 0.45rem;
	color: #fff;
}
/* Flag of Norway as an inline SVG (the white and blue crosses share one
   coordinate system, so the blue is always centred in the white — no
   sub-pixel drift, scales crisply at any size). */
.kbd-nav-plate-flag {
	display: block;
	width: 1.4rem;
	height: auto;
}
.kbd-nav-plate-n {
	font-size: 0.7rem;
	font-weight: 700;
	line-height: 1;
}
.kbd-nav-plate-chars {
	display: flex;
	align-items: center;
	padding: 0 1rem;
	font-size: 2.6rem;
	font-weight: 800;
	letter-spacing: 0.15rem;
	color: #111;
}
`;

// Kept as a JS string (not inline JSX text) so the apostrophe in "it's" does not
// trip react/no-unescaped-entities.
const MESSAGE = "🏎️💨 Thank you all for the ride, it's been a blast!";
const TENURE = "2021–2026";
const PLATE_TEXT = "THOMAS";

const backdropStyle: CSSProperties = {
	position: "fixed",
	inset: 0,
	width: "100%",
	height: "100%",
	border: "none",
	padding: 0,
	margin: 0,
	cursor: "pointer",
	background: "rgba(0, 0, 0, 0.55)",
	zIndex: Z_BACKDROP,
};

const cardStyle: CSSProperties = {
	position: "fixed",
	top: "50%",
	left: "50%",
	transform: "translate(-50%, -50%)",
	maxWidth: "90vw",
	padding: "2rem 2.5rem",
	borderRadius: "1rem",
	background: "#fff",
	color: "#1a1a1a",
	fontSize: "1.25rem",
	lineHeight: 1.5,
	textAlign: "center",
	boxShadow: "0 10px 40px rgba(0, 0, 0, 0.35)",
	zIndex: Z_CARD,
	pointerEvents: "none",
};

const EMOJI_COUNT = 70;

const CONFETTI_COUNT = 160;
const CONFETTI_COLORS = ["#ff3b30", "#ff9500", "#ffcc00", "#34c759", "#007aff", "#af52de", "#ff2d92", "#5ac8fa"];
const CONFETTI_EMOJI = ["🎊", "🎉"];

/** True when the user has asked for reduced motion (always false on the server). */
function prefersReducedMotion(): boolean {
	return typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Three independent deterministic values in [0, 1) derived from an index — no
 * `Math.random`, so the field is stable across renders. The three multipliers /
 * offsets are decorrelated, so a caller can drive three independent dimensions
 * (e.g. position, speed, phase) from one index without them sharing a pattern.
 */
function hashTriple(i: number): [number, number, number] {
	return [
		(((i * 2654435761) >>> 0) % 1000) / 1000,
		(((i * 40503 + 12345) >>> 0) % 1000) / 1000,
		(((i * 2246822519 + 374761) >>> 0) % 1000) / 1000,
	];
}

function Plate() {
	return (
		<div className="kbd-nav-plate">
			<div className="kbd-nav-plate-band" aria-hidden="true">
				{/* Flag of Norway, official construction (viewBox 22×16, bands 6:1:2:1:12 / 6:1:2:1:6). */}
				<svg className="kbd-nav-plate-flag" viewBox="0 0 22 16" shapeRendering="crispEdges">
					<rect width="22" height="16" fill="#ba0c2f" />
					<rect x="6" width="4" height="16" fill="#fff" />
					<rect y="6" width="22" height="4" fill="#fff" />
					<rect x="7" width="2" height="16" fill="#00205b" />
					<rect y="7" width="22" height="2" fill="#00205b" />
				</svg>
				<span className="kbd-nav-plate-n">N</span>
			</div>
			<div className="kbd-nav-plate-chars">{PLATE_TEXT}</div>
		</div>
	);
}

/**
 * Global keyboard-sequence listener mounted once in the app shell. Renders
 * nothing until its configured sequence is entered, then portals a transient,
 * self-cleaning overlay to `document.body`; dismissed by click or `Escape`.
 * Returns `null` on the server and before activation, so SSR/hydration output
 * is unchanged.
 */
export function KeyboardNav() {
	const [active, setActive] = useState(false);

	useKeySequence(SEQUENCE, () => setActive(true));

	useEffect(() => {
		if (!active) return;

		const style = document.createElement("style");
		style.id = OVERLAY_STYLE_ID;
		style.textContent = OVERLAY_ANIM_CSS;
		document.head.appendChild(style);

		function handleKeyDown(event: KeyboardEvent) {
			if (event.key === "Escape") setActive(false);
		}
		window.addEventListener("keydown", handleKeyDown);

		return () => {
			window.removeEventListener("keydown", handleKeyDown);
			style.remove();
		};
	}, [active]);

	if (!active) return null;

	// Honour prefers-reduced-motion: skip the continuous car rain and the confetti
	// burst entirely (rather than freezing them in place), leaving the static card.
	const reduce = prefersReducedMotion();

	const emoji = reduce
		? []
		: Array.from({ length: EMOJI_COUNT }, (_, i) => {
				// Independence is the whole point: tying position, speed, and phase to one
				// hash made columns share timing and blink empty between passes. With x,
				// speed, and phase decorrelated, the field stays uniformly full — a
				// continuous rain with no gaps.
				const [rx, rs, rp] = hashTriple(i); // horizontal position, fall speed, cycle phase
				// Random x across the viewport — NOT locked to columns, so multiple cars can
				// occupy the same band and cover for one another as they cycle.
				const left = rx * 100;
				const duration = 3 + rs * 3; // continuous speeds, 3–6s
				// Phase spread uniformly across the FULL cycle, independent of speed: a
				// negative delay of up to one whole duration places each car at a different
				// point of its fall right now, and keeps re-entries evenly staggered.
				const delay = -rp * duration;
				return (
					<span
						key={i}
						className="kbd-nav-emoji"
						aria-hidden="true"
						style={{ left: `${left}vw`, animationDuration: `${duration}s`, animationDelay: `${delay}s` }}
					>
						🏎️💨
					</span>
				);
			});

	const confetti = reduce
		? []
		: Array.from({ length: CONFETTI_COUNT }, (_, i) => {
				const [r1, r2, r3] = hashTriple(i);
				// All pieces launch from the SAME origin (bottom-center) and fan outward:
				// spread the launch angle across a 130° cone centered on straight-up, then
				// resolve angle + speed into the up-and-out displacement.
				const spreadDeg = 130;
				// Even base angle across the cone, plus a small jitter so the fan looks like
				// a cannon burst rather than a rigid peacock tail.
				const jitterDeg = (r2 - 0.5) * (spreadDeg / CONFETTI_COUNT) * 4;
				const angleDeg = -90 + (i / (CONFETTI_COUNT - 1) - 0.5) * spreadDeg + jitterDeg; // up-and-out
				const angleRad = (angleDeg * Math.PI) / 180;
				const speed = 45 + r1 * 50; // travel distance, viewport units (wide range)
				const dx = Math.cos(angleRad) * speed; // vw — left/right reach of the fan
				const dy = Math.sin(angleRad) * speed; // vh — negative = upward
				const spin = (r2 < 0.5 ? -1 : 1) * (360 + r1 * 1080); // 1–4 turns, both directions
				const duration = 1.4 + r3 * 1.8; // varied hang time

				// Mix two kinds of confetti: ~1 in 4 pieces is a 🎊/🎉 emoji, the rest are
				// little colored rectangles of varying size/shape.
				const isEmoji = r3 < 0.25;
				const shared = {
					left: "50vw",
					animationDuration: `${duration}s`,
					"--dx": `${dx}vw`,
					"--dy": `${dy}vh`,
					"--spin": `${spin}deg`,
				};
				if (isEmoji) {
					return (
						<span
							key={i}
							className="kbd-nav-confetti"
							aria-hidden="true"
							style={{ ...shared, fontSize: `${1.1 + r1 * 1.4}rem` } as CSSProperties}
						>
							{CONFETTI_EMOJI[i % CONFETTI_EMOJI.length]}
						</span>
					);
				}
				const w = 0.4 + r1 * 0.6; // 0.4–1.0rem
				const h = 0.5 + r2 * 0.8; // 0.5–1.3rem (varied aspect ratio)
				return (
					<span
						key={i}
						className="kbd-nav-confetti"
						aria-hidden="true"
						style={
							{
								...shared,
								width: `${w}rem`,
								height: `${h}rem`,
								borderRadius: r2 < 0.3 ? "50%" : "1px", // some circles, some chips
								background: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
							} as CSSProperties
						}
					/>
				);
			});

	return createPortal(
		<>
			<button type="button" aria-label="Dismiss" style={backdropStyle} onClick={() => setActive(false)} />
			{confetti}
			{emoji}
			<div style={cardStyle} role="status">
				<Plate />
				<div style={{ marginTop: "0.5rem", fontSize: "1.4rem", fontWeight: 600, color: "#555" }}>{TENURE}</div>
				<div style={{ marginTop: "0.75rem" }}>{MESSAGE}</div>
			</div>
		</>,
		document.body,
	);
}
